import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official/dist/highcharts-react";
import classes from './Trends.module.css';
import { selectionsSliceActions } from "../store/slices/selectionsSlice";
import { useDispatch, useSelector } from 'react-redux';

const Trends = () => {
    const dispatch = useDispatch();
    const chartRef = useRef(null);
    const trendtype = useSelector(state => state.selection.trend);
    const globalTrend = useSelector(state => state.agg.global_annual_trend);
    const regionalTrend = useSelector(state => state.agg.annual_region_trend);
    const driverTrend = useSelector(state => state.agg.annual_driver_trend);

    // Track whether every / no series is currently visible so the legend
    // controls can disable the redundant action (all shown -> "Select all" off,
    // none shown -> "Unselect all" off). Synced from both the buttons and
    // legend clicks via the series show/hide events below.
    const [allVisible, setAllVisible] = useState(true);
    const [noneVisible, setNoneVisible] = useState(false);

    const syncVisibility = useCallback(() => {
        const chart = chartRef.current?.chart;
        const series = chart?.series ?? [];
        if (!series.length) {
            setAllVisible(true);
            setNoneVisible(false);
            return;
        }
        const visibleCount = series.filter((s) => s.visible).length;
        setAllVisible(visibleCount === series.length);
        setNoneVisible(visibleCount === 0);
    }, []);

    const toNumber = (value) => {
        if (value == null) {
            return NaN;
        }
        if (typeof value === "number") {
            return value;
        }
        if (typeof value === "string") {
            const cleaned = value.replace(/,/g, "").trim();
            return cleaned === "" ? NaN : Number(cleaned);
        }
        return Number(value);
    };

    const selectTrend = (trend) => {
        dispatch(selectionsSliceActions.setTrend(trend));
    };

    const trendTitle = trendtype === "regional"
        ? "Regional IDPs Trends"
        : trendtype === "driver"
            ? "Driver IDPs Trends"
            : "Global IDPs Trends";

    const trendGrouped = useMemo(() => {
        const source = trendtype === "regional"
            ? regionalTrend
            : trendtype === "driver"
                ? driverTrend
                : globalTrend;

        if (!Array.isArray(source)) {
            return { groups: new Map(), groupLabels: new Map() };
        }

        const groups = new Map();
        const groupLabels = new Map();

        source.forEach((item) => {
            const year = item?.year ?? item?.Year ?? item?.YEAR;
            const total =
                item?.total ??
                item?.value ??
                item?.total_idps ??
                item?.idps ??
                item?.count;
            const totalNumber = toNumber(total);

            const yearNumber = toNumber(year);

            if (!Number.isFinite(yearNumber) || !Number.isFinite(totalNumber)) {
                return;
            }

            let group = "Global";
            if (trendtype === "regional") {
                group = item?.region ?? item?.Region ?? item?.REGION ?? "Unknown";
            } else if (trendtype === "driver") {
                group =
                    item?.driver ??
                    item?.Driver ??
                    item?.reason_for_displacement ??
                    item?.displacement_reason ??
                    item?.reason ??
                    item?.cause ??
                    "Unknown";
            }

            const rawLabel = String(group).trim() || "Unknown";
            const groupKey = rawLabel.toLowerCase();
            if (!groupLabels.has(groupKey)) {
                groupLabels.set(groupKey, rawLabel);
            }
            const yearKey = yearNumber;
            const yearTotals = groups.get(groupKey) ?? new Map();
            const prevTotal = yearTotals.get(yearKey) ?? 0;
            yearTotals.set(yearKey, prevTotal + totalNumber);
            groups.set(groupKey, yearTotals);
        });

        return { groups, groupLabels };
    }, [trendtype, globalTrend, regionalTrend, driverTrend]);

    const globalSeries = useMemo(() => {
        if (!Array.isArray(globalTrend)) {
            return [];
        }

        const items = globalTrend
            .map((item) => {
                const year = item?.year ?? item?.Year ?? item?.YEAR;
                const total =
                    item?.total ??
                    item?.value ??
                    item?.total_idps ??
                    item?.idps ??
                    item?.count;
                const totalNumber = toNumber(total);

                const yearNumber = toNumber(year);

                if (!Number.isFinite(yearNumber) || !Number.isFinite(totalNumber)) {
                    return null;
                }

                return { year: yearNumber, total: totalNumber };
            })
            .filter(Boolean)
            .sort((a, b) => a.year - b.year)
            .map((item, index, arr) => {
                const prev = arr[index - 1];
                const delta = prev ? ((item.total - prev.total) / prev.total) * 100 : null;
                return {
                    x: item.year,
                    y: Number((item.total / 1000000).toFixed(2)),
                    total: item.total,
                    delta,
                };
            });

        return items.length
            ? [{
                name: "Global",
                data: items,
                color: "#0033A0",
                marker: { enabled: items.length === 1, symbol: "circle" },
            }]
            : [];
    }, [globalTrend]);

    const trendSeries = useMemo(() => {
        const groupedSeries = Array.from(trendGrouped.groups.entries()).map(([key, yearTotals]) => {
            const name = trendGrouped.groupLabels.get(key) ?? key;
            const sorted = Array.from(yearTotals.entries())
                .map(([year, total]) => ({ year, total }))
                .sort((a, b) => a.year - b.year);
            const data = sorted.map((item, index) => {
                const prev = sorted[index - 1];
                const delta = prev ? ((item.total - prev.total) / prev.total) * 100 : null;
                return {
                    x: item.year,
                    y: Number((item.total / 1000000).toFixed(2)),
                    total: item.total,
                    delta,
                };
            });

            return {
                name: trendtype === "global" ? "IDPs (M)" : name,
                data,
                marker: { enabled: data.length === 1, symbol: "circle" },
            };
        });

        if (trendtype === "global") {
            return globalSeries.length ? globalSeries : groupedSeries;
        }

        return [...globalSeries, ...groupedSeries];
    }, [trendGrouped, trendtype, globalSeries]);

    const yearRange = useMemo(() => {
        const xs = trendSeries
            .flatMap((series) => series.data.map((point) => point.x))
            .filter(Number.isFinite);
        if (!xs.length) return null;
        return { min: Math.min(...xs), max: Math.max(...xs) };
    }, [trendSeries]);

    const trendOptions = useMemo(() => ({
        chart: {
            type: trendtype === "global" ? "area" : "line",
            height: 200,
            backgroundColor: "transparent",
            spacing: [15, 10, 5, 10],
        },
        title: { text: null },
        credits: { enabled: false },
        legend: { enabled: trendSeries.length > 1 },
        xAxis: {
            tickmarkPlacement: "on",
            lineColor: "#9fb4e3",
            tickColor: "#9fb4e3",
            labels: { style: { color: "#5f6b7a" } },
            plotLines: [],
        },
        yAxis: {
            title: { text: null },
            gridLineColor: "#e8eef8",
            labels: {
                style: { color: "#5f6b7a" },
                formatter: function () {
                    return this.value === 0 ? "0" : `${this.value}M`;
                },
            },
        },
        tooltip: {
            useHTML: true,
            borderWidth: 0,
            shadow: false,
            backgroundColor: "transparent",
            formatter: function () {
                const trendLabel = trendtype === "regional"
                    ? "Regional"
                    : trendtype === "driver"
                        ? "Driver"
                        : "Global";
                const seriesLabel = this.series?.name ? ` · ${this.series.name}` : "";
                return `
                    <div class="${classes.tooltipCard}">
                                                <span class="${classes.tooltipText}">${trendLabel}${seriesLabel}</span>

                        <div class="${classes.tooltipHeader}">
                            <div>
                                <span class="${classes.tooltipYear}">${this.x}</span>
                                ${this.point.delta === null ? "" : `
                                    <span class="${classes.tooltipDelta}">
                                        <span class="${classes.triangle} ${this.point.delta < 0 ? classes.triangleDown : ""}"></span>
                                        ${Math.abs(this.point.delta).toFixed(1)}% ${this.point.delta >= 0 ? "Increased" : "Decreased"}
                                    </span>
                                `}
                            </div>
                        </div>
                        <div class="${classes.tooltipBody}">
                            <img class="${classes.tooltipFigure}" src="/images/total_idps_blue.png" alt="Total IDPs" />
                            <div class="${classes.tooltipTotal}">
                                ${this.point.total.toLocaleString()}
                                <span class="${classes.tooltipText}">Total IDPs</span>
                            </div>
                        </div>
                       
                    </div>
                `;
            },
        },
        plotOptions: {
            series: {
                marker: {
                    enabled: false,
                    radius: 4,
                    lineWidth: 2,
                    symbol: "circle",
                    states: {
                        hover: { enabled: true, radius: 4 },
                    },
                },
                states: {
                    hover: {
                        lineWidth: 2,
                    },
                },
                events: {
                    show: syncVisibility,
                    hide: syncVisibility,
                },
            },
            area: trendtype === "global" && trendSeries.length === 1 ? {
                lineWidth: 2,
                color: "#0033A0",
                fillColor: {
                    linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
                    stops: [
                        [0, "rgba(45, 96, 216, 0.2)"],
                        [1, "rgba(45, 96, 216, 0.02)"],
                    ],
                },
                dataLabels: {
                    enabled: true,
                    formatter: function () {
                        return this.point.y === this.series.dataMax ? `${this.y}M` : null;
                    },
                    style: {
                        color: "#0033A0",
                        fontWeight: "700",
                        fontSize: "12px",
                        textOutline: "none",
                    },
                    y: -8,
                },
            } : {},
        },
        series: trendSeries,
    }), [trendSeries, trendtype, syncVisibility]);

    // Re-rendering the chart for a new trend type / dataset shows all series
    // again, so reset the controls to "all selected".
    useEffect(() => {
        setAllVisible(true);
        setNoneVisible(false);
    }, [trendtype, trendSeries]);

    const setAllSeriesVisible = (visible) => {
        const chart = chartRef.current?.chart;
        if (!chart) return;
        chart.series.forEach((series) => {
            series.setVisible(visible, false);
        });
        chart.redraw();
        syncVisibility();
    };

    return (
        <>
            <div className={classes.box}>
                <div className={classes.trends_header}>
                    <div className={classes.trends_box}>
                        <p className={classes.title}>{trendTitle}{yearRange ? ` ${yearRange.min} - ${yearRange.max}` : ""}</p>
                        <p className={classes.subtitle}>IDPs figure taken from the last data collection round in a given year and country.</p>
                    </div>
                    <div className={classes.trends_buttons}>
                        <button
                            className={`${classes.trend_button} ${classes.global_trend} ${trendtype === "global" ? classes.trend_button_active : classes.trend_button_inactive}`}
                            onClick={() => selectTrend("global")}
                        >
                            Global Trend
                        </button>
                        <button
                            className={`${classes.trend_button} ${classes.regional_trend} ${classes.middle_trend} ${trendtype === "regional" ? classes.trend_button_active : classes.trend_button_inactive}`}
                            onClick={() => selectTrend("regional")}
                        >
                            Regional Trend
                        </button>
                        <button
                            className={`${classes.trend_button} ${classes.driver_trend} ${trendtype === "driver" ? classes.trend_button_active : classes.trend_button_inactive}`}
                            onClick={() => selectTrend("driver")}
                        >
                            Driver Trend
                        </button>
                    </div>
                </div>
                {trendtype !== "global" && (
                    <div className={classes.trends_legend_controls}>
                        <button
                            type="button"
                            className={classes.trends_control_button}
                            onClick={() => setAllSeriesVisible(true)}
                            disabled={allVisible}
                        >
                            Select all
                        </button>
                        <button
                            type="button"
                            className={classes.trends_control_button}
                            onClick={() => setAllSeriesVisible(false)}
                            disabled={noneVisible}
                        >
                            Unselect all
                        </button>
                    </div>
                )}
                <div className={classes.trend_chart}>
                    <HighchartsReact highcharts={Highcharts} options={trendOptions} ref={chartRef} />
                </div>


            </div>


        </>
    )
}

export default Trends
